/**
 * Direction A, desktop: the lifeline as a metro map. Each line is a lane; a change is a
 * station; several lines changing in one transaction is an interchange. Findings hang from
 * the rail at the top and drop a leader to the check they point at. SVG, one scale per axis:
 * time across (lifelineModel.timeScale), and the Pay lane's rate on its own vertical scale.
 * Every part a reader has to decode carries a <Tip> (an overlay outside the SVG: it never
 * moves a label).
 */
import {
  LINES, axisTicks, dayMs, firstDay, fitText, fmtDate, fmtDays, fmtMoney, isoDay, labelBox, laneLabels, linear,
  offerCheckText, onboardingReason, onboardingSegments, payMetric, payText, placeLabel, preHireParts, segmentLabel, stepBars, textWidth, timeScale,
} from './lifelineModel'
import { Tip } from './Tip'

const W = 1200
const LEFT = 24
const RIGHT = 1100
const PRE_W = 190
const Y = { rail: 46, job: 112, payTop: 150, payBottom: 226, manager: 270, location: 318, status: 366, onboarding: 414, axis: 452 }
const H = 480
const COLOR = {
  job: 'var(--ll-job)', pay: 'var(--ll-pay)', manager: 'var(--ll-manager)', location: 'var(--ll-location)',
  status: 'var(--ll-status)', onboarding: 'var(--ll-onboarding)',
}

// The map's guidance (its tooltips), in plain words.
const ZONE_HELP = 'Magnified: the hiring journey before day one. The requisition, each recruiting step as a bar by days (the tick is the typical time for that step) and the offer. Not to the same scale as employment.'
const TODAY_HELP = 'Today: the day this record was read. Lines still open run to here.'
const RAIL_HELP = 'The findings rail: each marker is an open finding that names this person, at its date. A triangle is high severity, a diamond medium or low; its ring on the Pay line marks the check it points at.'
const ONB_HELP = 'The Onboarding line: the new-hire tasks after the hire, from the onboarding record. Solid when finished, dashed while still open.'
const PAY_HELP = 'The Pay line: base rate over time, each step a pay change. Click to open the pay strata, every check for this person.'

function stationHelp(st, lines, pill) {
  const names = lines.map(l => LINES.find(x => x.key === l)?.label || l)
  return pill
    ? `${fmtDate(st.date)}: several things changed on this date (${names.join(', ')}), in one transaction. Click to see what changed.`
    : `${fmtDate(st.date)}: a change on this date: ${st.title}. Click to see what changed.`
}

function findingHelp(m) {
  const sev = m.severity === 'high' ? 'High' : m.severity === 'medium' ? 'Medium' : 'Low'
  return `${sev} finding: ${m.finding.title || 'finding'}.${m.check ? ` The ring marks the check paid ${fmtDate(m.check.pay_date)}.` : ''} Click to see the evidence.`
}

function activate(fn) {
  return {
    onClick: fn,
    onKeyDown: (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fn() } },
  }
}

export default function LifelineMap({ data, stations, markers, hidden, peersOn, selected, onSelect, onOpenPay }) {
  const s = data.sections
  const today = data.as_of
  // employment starts at the FIRST thing on record (a rehire's earlier stint is employment,
  // never squeezed into the magnified before-hire zone)
  const hire = firstDay(data) || data.person.last_hire_date || today
  const pre = s.pre_hire?.available ? s.pre_hire : null
  // the zone shows the hire whose journey ends where employment starts (a rehire's later
  // journey is in the before-hire panel); none within 45 days -> the zone says so
  const zoneHire = pre ? ((pre.hires || [pre])
    .map(h => ({ h, gap: Math.abs(dayMs(h.hire_date) - dayMs(hire)) }))
    .filter(c => c.gap <= 45 * 86400000).sort((a, b) => a.gap - b.gap)[0]?.h || null) : null
  const parts = preHireParts(zoneHire)
  const bars = zoneHire ? stepBars(zoneHire) : []
  const onb = parts?.onboarding
  const onbSegs = onboardingSegments(pre, today)    // every hire's onboarding, not just the zone's
  const offer = parts?.offer
  const zoneNote = !pre ? (s.pre_hire?.reason || 'No recruiting record links to this person.')
    : !zoneHire ? `Hired through recruiting ${fmtDate(pre.hire_date)}: the journey is in the before-hire panel below.` : null
  const earliest = [parts?.requisition?.opened_on, parts?.application?.applied_on, onb?.started_on]
    .map(dayMs).filter(v => v != null && v < dayMs(hire))
  const preStart = earliest.length ? isoDay(Math.min(...earliest)) : isoDay(dayMs(hire) - 45 * 86400000)
  // one row per step in the magnified zone, between the requisition label and the offer row
  const rowH = Math.min(22, (Y.status - 20 - 92) / Math.max(1, bars.length))
  const rowY = (i) => 92 + i * rowH
  const t = timeScale({ start: preStart, hire, end: today, left: LEFT, right: RIGHT, preWidth: PRE_W })
  const xEnd = (seg) => t.x(seg.end || today)

  // Pay lane: its own scale (rate), padded, and wide enough for the peer band when shown
  const paySegs = s.pay?.available ? s.pay.segments : []
  const metric = payMetric(paySegs, data.person)      // a salaried person's line is their salary
  const vals = paySegs.map(g => g[metric]).filter(v => v != null)
  // the band compares like with like: drawn only on the scale the peers were compared on
  const peer = peersOn && s.peers?.available && (s.peers.metric || 'hourly_rate') === metric ? s.peers[metric] : null
  if (peer) vals.push(peer.p25, peer.p75)
  const lo = vals.length ? Math.min(...vals) : 0
  const hi = vals.length ? Math.max(...vals) : 1
  const pad = (hi - lo) * 0.12 || 1
  const py = linear(lo - pad, hi + pad, Y.payBottom, Y.payTop)
  const fmtRate = (v) => metric === 'hourly_rate' ? `${fmtMoney(v)}/h` : `${fmtMoney(v, 0)}/yr`
  const payLabel = (g) => payText(g, data.person?.pay_type)        // each segment in its own terms

  const laneY = (line, seg) => line === 'pay' ? (seg && seg[metric] != null ? py(seg[metric]) : (Y.payTop + Y.payBottom) / 2) : Y[line]
  const visible = LINES.filter(l => !hidden.has(l.key))
  const lastPay = paySegs[paySegs.length - 1]
  const zoneW = t.empLeft - LEFT - 6            // labels in the before-hire zone stay inside it
  // the axis: the hire date at the left and "Today" at the right keep their room; year
  // ticks are thinned until no two labels touch (lifelineModel.axisTicks)
  const hireLabel = fmtDate(hire), todayLabel = `Today · ${fmtDate(today)}`
  const ticks = axisTicks(t.years, { reserved: [
    { x0: t.empLeft, x1: t.empLeft + textWidth(hireLabel, 10.5) },
    { x0: RIGHT - textWidth(todayLabel, 10.5), x1: RIGHT }] })

  // stations: a ring per line, or one pill across the lines an interchange changed
  const stationMarks = stations.map(st => {
    const x = t.x(st.date)
    const lines = st.lines.filter(l => !hidden.has(l) && s[l]?.available)
    if (x == null || !lines.length) return null
    const ys = lines.map(l => laneY(l, (s[l].segments || []).find(g => g.start === st.date) || (s[l].segments || [])[0]))
    const pill = st.interchange && lines.length > 1
    return { st, x, lines, ys, pill, top: Math.min(...ys), bottom: Math.max(...ys) }
  }).filter(Boolean)
  const markBoxes = stationMarks.flatMap(m => (m.pill
    ? [{ x0: m.x - 8, x1: m.x + 8, y0: m.top - 8, y1: m.bottom + 8 }]
    : m.ys.map(y => ({ x0: m.x - 8, x1: m.x + 8, y0: y - 8, y1: y + 8 }))))

  // the peer band's lines (p75, median, p25): nothing is written on them
  const bandX = peer && lastPay ? t.x(lastPay.start) : null
  const peerLines = bandX == null ? [] : [peer.p75, peer.median, peer.p25]
    .map(v => ({ x0: bandX, x1: RIGHT, y0: py(v) - 1.5, y1: py(v) + 1.5 }))

  // lane labels, laid out once for the whole map (lifelineModel.laneLabels): a label ends
  // before the next segment and before an interchange drawn across its row; a pay label
  // goes under its line when above it would sit on a peer line
  const lanes = {}
  const labelBoxes = []
  for (const { key } of visible) {
    const sec = s[key]
    if (key === 'onboarding' || !sec?.available) continue
    const segs = sec.segments
    const rows = segs.map(g => {
      const y = laneY(key, g)
      if (key !== 'pay') return y + 17
      const spots = [y - 9, y + 17].map(yy => ({ x: t.x(g.start) + 8, y: yy, text: payLabel(g) }))
      return (placeLabel(spots, peerLines) || spots[0]).y
    })
    // a label's glyphs run from about 8 px above its baseline to 2 below
    const crosses = (m, yb) => Math.min(m.bottom + 8, yb + 2) - Math.max(m.top - 8, yb - 8) >= 3
    const labels = laneLabels(segs.map((g, i) => ({
      x: t.x(g.start),
      text: key === 'pay' ? payLabel(g) : segmentLabel(key, g),
      stops: stationMarks.filter(m => m.pill && crosses(m, rows[i])).map(m => m.x - 8),
    })), { right: RIGHT, px: 11 })
    lanes[key] = labels.map((l, i) => ({ ...l, y: rows[i] }))
    for (const l of lanes[key]) if (l.text) labelBoxes.push(labelBox({ x: l.x, y: l.y, text: l.text }))
  }

  // the peer band's label goes where it sits on nothing: above the band or under it, from
  // its start or against Today, in full or short; when nowhere is clear it is not drawn
  // (the band's tooltip and the Compare to peers card say the same)
  const peerFull = peer ? `Peers p25–p75 · median dashed · n = ${s.peers.n}` : ''
  const peerHelp = peer ? `The shaded band: the middle half of ${metric === 'annual_salary' ? 'annual salaries of salaried' : 'hourly rates of hourly'} people in the same job and work location (25th to 75th percentile, ${fmtRate(peer.p25)} to ${fmtRate(peer.p75)}); the dashed line is their median, ${fmtRate(peer.median)}. ${s.peers.n} peers.` : ''
  let peerLabel = null
  if (bandX != null) {
    const payLine = paySegs.flatMap((g, i) => {
      const y = laneY('pay', g), x1 = t.x(g.start), x2 = xEnd(g)
      const prev = i > 0 ? laneY('pay', paySegs[i - 1]) : y
      return [{ x0: x1, x1: x2, y0: y - 3, y1: y + 3 }, { x0: x1 - 3, x1: x1 + 3, y0: Math.min(prev, y), y1: Math.max(prev, y) }]
    })
    const rings = markers.map(m => (m.day ? t.x(m.check?.pay_date || m.day) : null)).filter(x => x != null)
      .map(x => ({ x0: x - 10, x1: x + 10, y0: Y.payTop - 10, y1: Y.payBottom + 10 }))
    const top = py(peer.p75), bottom = py(peer.p25)
    const spots = text => [
      { x: bandX + 6, y: top - 7 }, { x: RIGHT - 6, y: top - 7, anchor: 'end' },
      { x: bandX + 6, y: bottom + 15 }, { x: RIGHT - 6, y: bottom + 15, anchor: 'end' },
    ].map(o => ({ ...o, text, px: 10.5 }))
    peerLabel = placeLabel([...spots(peerFull), ...spots(`Peers · n = ${s.peers.n}`)],
      [...labelBoxes, ...markBoxes, ...peerLines, ...payLine, ...rings],
      { x0: t.empLeft + 2, x1: RIGHT - 4, y0: Y.job + 4, y1: Y.manager - 4 })
  }

  return (
    <div className="ll-map-wrap">
      <svg className="ll-map" viewBox={`0 0 ${W} ${H}`} role="group" aria-label="Lifeline map: lines for job, pay, manager, location, status and onboarding; stations are changes; markers on the top rail are findings">
        <defs>
          <pattern id="ll-hatch" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <rect width="6" height="6" fill="#fff" />
            <line x1="0" y1="0" x2="0" y2="6" stroke="#8a929c" strokeWidth="2" />
          </pattern>
        </defs>

        {/* before-hire zone: magnified (one scale, see timeScale) -- requisition, the steps
            as bars by days with the peer-median tick, the offer, or why none is shown */}
        {t.hasPre && (
          <Tip text={ZONE_HELP} anchor=".ll-zone-label" order={['right', 'bottom', 'top']}>
          <g className="ll-zone" tabIndex={0} role="group" aria-label="Before hire, magnified">
            <rect x={0} y={0} width={t.empLeft} height={H} fill="var(--ll-prehire-bg)" />
            <rect className="ll-focus-ring" x={2} y={2} width={t.empLeft - 4} height={H - 4} rx={6} />
            <text className="ll-zone-label" x={LEFT} y={20}>BEFORE HIRE · MAGNIFIED</text>
            {zoneNote && (
              <foreignObject x={LEFT} y={Y.job - 12} width={PRE_W - 12} height={120}>
                <p className="ll-map-note">{zoneNote}</p>
              </foreignObject>
            )}
            {parts?.requisition && (
              <g>
                <line x1={t.x(parts.requisition.opened_on)} x2={t.x(parts.requisition.opened_on)} y1={60} y2={Y.status} stroke="var(--ll-recruiting)" strokeDasharray="2 3" />
                <text x={LEFT} y={74} style={{ fontSize: 10.5, fill: 'var(--ll-recruiting)', fontWeight: 600 }}>
                  {fitText(`${parts.requisition.number ? `${parts.requisition.number} · ` : ''}opened ${fmtDate(parts.requisition.opened_on)}`, zoneW, 10.5)}
                </text>
              </g>
            )}
            {bars.map(b => {
              const x1 = t.x(b.from), x2 = Math.max(x1 + 3, t.x(b.to)), y = rowY(b.i)
              const mx = b.median != null ? t.x(isoDay(dayMs(b.from) + b.median * 86400000)) : null
              return (
                <g key={b.i} role="img" aria-label={`${b.name}: ${b.days != null ? `${b.days} days` : 'current step'}${b.median != null ? `, median ${b.median} days` : ''}`}>
                  <text x={LEFT} y={y - 3} style={{ fontSize: 10, fill: 'var(--gray-700)' }}>
                    <title>{b.name}</title>
                    {fitText(`${b.name}${b.days != null ? ` · ${fmtDays(b.days)}` : ''}`, zoneW, 10)}
                  </text>
                  <rect x={x1} y={y} width={x2 - x1} height={7} rx={2} fill={b.slow ? 'var(--ll-medium)' : 'var(--ll-recruiting)'} opacity={b.open ? 0.45 : 1} />
                  {mx != null && <line x1={mx} x2={mx} y1={y - 2} y2={y + 9} stroke="var(--ll-ink)" strokeWidth={1.5} />}
                </g>
              )
            })}
            {offer && (
              <g role="img" aria-label={offerCheckText(offer) || 'Offer'}>
                {[offer.sent_on, offer.accepted_on].map((d, i) => (d && t.x(d) != null && dayMs(d) < dayMs(hire)
                  ? <path key={i} d={`M${t.x(d)} ${Y.status - 6} l5 5 l-5 5 l-5 -5 Z`} fill={i ? 'var(--ll-recruiting)' : '#fff'} stroke="var(--ll-recruiting)" strokeWidth={1.5} />
                  : null))}
                <text x={LEFT} y={Y.status + 18} style={{ fontSize: 10, fill: offer.vs_first_check?.differs ? 'var(--ll-high)' : 'var(--gray-700)', fontWeight: offer.vs_first_check?.differs ? 700 : 400 }}>
                  {fitText(offer.vs_first_check?.differs ? 'Offer ≠ first paycheck' : 'Offer sent · accepted', zoneW, 10)}
                </text>
              </g>
            )}
          </g>
          </Tip>
        )}
        <text className="ll-zone-label" x={t.empLeft + 10} y={18} style={{ fill: 'var(--ll-muted)' }}>EMPLOYMENT · TO SCALE</text>

        {/* year grid */}
        {ticks.map(y => (
          <g key={y.label}>
            <line x1={y.x} x2={y.x} y1={60} y2={Y.axis - 6} stroke="#eceee9" />
            <text className="ll-axis" x={y.x} y={Y.axis + 14} textAnchor="middle">{y.label}</text>
          </g>
        ))}
        <text className="ll-axis" x={t.empLeft} y={Y.axis + 14}>{hireLabel}</text>
        <Tip text={TODAY_HELP} order={['left', 'top', 'bottom']}>
          <g className="ll-today" tabIndex={0} role="img" aria-label={todayLabel}>
            <rect x={RIGHT - 6} y={60} width={12} height={Y.axis - 66} fill="transparent" />
            <text className="ll-axis" x={RIGHT} y={Y.axis + 14} textAnchor="end" style={{ fontWeight: 600, fill: 'var(--ll-ink)' }}>{todayLabel}</text>
            <line x1={RIGHT} x2={RIGHT} y1={60} y2={Y.axis - 6} stroke="var(--ll-ink)" strokeDasharray="4 3" strokeWidth={1.5} />
            <rect className="ll-focus-ring" x={RIGHT - textWidth(todayLabel, 10.5) - 4} y={Y.axis + 2} width={textWidth(todayLabel, 10.5) + 8} height={16} rx={4} />
          </g>
        </Tip>

        {/* findings rail */}
        <line x1={LEFT} x2={RIGHT} y1={Y.rail} y2={Y.rail} stroke="#d3d6cf" strokeDasharray="4 3" />
        <Tip text={RAIL_HELP}>
          <g tabIndex={0} role="img" aria-label="Findings rail">
            <text className="ll-axis" x={t.empLeft + 10} y={Y.rail - 10} style={{ letterSpacing: '0.08em' }}>FINDINGS</text>
            <rect className="ll-focus-ring" x={t.empLeft + 6} y={Y.rail - 22} width={textWidth('FINDINGS', 10.5) + 16} height={16} rx={4} />
          </g>
        </Tip>

        {/* peer band behind the pay line */}
        {peer && lastPay && (
          <Tip text={peerHelp}>
          <g aria-hidden="true">
            <rect x={bandX} width={RIGHT - bandX} y={py(peer.p75)} height={Math.max(2, py(peer.p25) - py(peer.p75))} fill="var(--ll-peer)" />
            <line x1={bandX} x2={RIGHT} y1={py(peer.median)} y2={py(peer.median)} stroke="var(--ll-peer-line)" strokeDasharray="5 3" strokeWidth={1.5} />
            {peerLabel && (
              <text x={peerLabel.x} y={peerLabel.y} textAnchor={peerLabel.anchor || 'start'} style={{ fontSize: 10.5, fill: 'var(--ll-peer-line)' }}>{peerLabel.text}</text>
            )}
          </g>
          </Tip>
        )}

        {/* lines */}
        {visible.map(({ key }) => {
          const sec = key === 'onboarding' ? null : s[key]
          if (key === 'onboarding') {
            const latest = onbSegs[onbSegs.length - 1]
            const label = latest ? latest.label : `Onboarding · ${onboardingReason(s.pre_hire)}`
            return (
              <Tip key={key} text={`${label}. ${ONB_HELP}`}>
              <g className="ll-onb" tabIndex={0} role="img" aria-label={label}>
                <rect x={t.empLeft} y={Y.onboarding - 10} width={RIGHT - t.empLeft} height={20} fill="transparent" />
                <rect className="ll-focus-ring" x={t.empLeft - 2} y={Y.onboarding - 22} width={RIGHT - t.empLeft + 4} height={32} rx={6} />
                <line x1={t.empLeft} x2={RIGHT} y1={Y.onboarding} y2={Y.onboarding} stroke={COLOR.onboarding} strokeOpacity={0.35} strokeWidth={2} strokeDasharray="2 4" />
                {onbSegs.map((g, i) => {
                  const x1 = t.x(g.from), x2 = t.x(g.to)
                  return x1 == null || x2 == null ? null : (
                    <line key={i} className="ll-onb-seg" x1={x1} x2={Math.max(x1 + 3, x2)} y1={Y.onboarding} y2={Y.onboarding} stroke={COLOR.onboarding}
                          strokeWidth={g.open ? 4 : 6} strokeDasharray={g.open ? '8 3' : undefined} />
                  )
                })}
                <text x={t.empLeft + 8} y={Y.onboarding - 8} style={{ fontSize: 11, fill: COLOR.onboarding }}>
                  {fitText(label, RIGHT - t.empLeft - 16)}
                </text>
              </g>
              </Tip>
            )
          }
          if (!sec?.available) {
            return (
              <g key={key}>
                <line x1={t.empLeft} x2={RIGHT} y1={laneY(key)} y2={laneY(key)} stroke={COLOR[key]} strokeOpacity={0.25} strokeWidth={2} strokeDasharray="6 4" />
                <text x={t.empLeft + 8} y={laneY(key) - 8} style={{ fontSize: 11, fill: 'var(--ll-muted)' }}>
                  <title>{sec?.reason || 'Not available'}</title>{fitText(sec?.reason || 'Not available', RIGHT - t.empLeft - 16)}
                </text>
              </g>
            )
          }
          const segs = sec.segments
          const labels = lanes[key]
          return (
            <g key={key}>
              {segs.map((g, i) => {
                const x1 = t.x(g.start), x2 = xEnd(g), y = laneY(key, g)
                const leave = key === 'status' && (g.code === 'L' || g.code === 'S')
                const term = key === 'status' && g.code === 'T'
                return (
                  <g key={i}>
                    {leave
                      ? <rect x={x1} y={y - 5} width={Math.max(2, x2 - x1)} height={10} fill="url(#ll-hatch)" stroke="#8a929c" />
                      : <line x1={x1} x2={x2} y1={y} y2={y} stroke={COLOR[key]} strokeWidth={key === 'status' ? 4 : 6} strokeLinecap="butt" strokeDasharray={term ? '3 4' : undefined} />}
                    {key === 'pay' && i > 0 && (
                      <line x1={x1} x2={x1} y1={laneY('pay', segs[i - 1])} y2={y} stroke={COLOR.pay} strokeWidth={5} />
                    )}
                    {labels[i].text && (
                      <text className="ll-seg-label" x={labels[i].x} y={labels[i].y} style={{ fill: COLOR[key] }}>
                        {labels[i].text !== labels[i].full && <title>{labels[i].full}</title>}
                        {labels[i].text}
                      </text>
                    )}
                  </g>
                )
              })}
            </g>
          )
        })}

        {/* the Pay line opens the pay strata (direction B) */}
        {!hidden.has('pay') && s.pay_statements?.available && (
          <Tip text={`${PAY_HELP}${peer ? ` ${peerHelp}` : ''}`}>
          <g className="ll-hit" tabIndex={0} role="button" aria-label="Open pay strata: every check for this person" {...activate(onOpenPay)}>
            <rect x={t.empLeft} y={Y.payTop - 6} width={RIGHT - t.empLeft} height={Y.payBottom - Y.payTop + 12} fill="transparent" />
            <rect className="ll-focus-ring" x={t.empLeft - 2} y={Y.payTop - 8} width={RIGHT - t.empLeft + 4} height={Y.payBottom - Y.payTop + 16} rx={6} />
          </g>
          </Tip>
        )}

        {/* stations and interchanges */}
        {stationMarks.map(({ st, x, lines, ys, pill, top, bottom }) => {
          const isSel = selected?.type === 'station' && selected.id === st.id
          return (
            <Tip key={st.id} text={stationHelp(st, lines, pill)}>
            <g className="ll-hit" tabIndex={0} role="button"
               aria-label={`${st.title}, ${fmtDate(st.date)}${st.interchange ? `, ${lines.length} lines changed in one transaction` : ''}`}
               aria-pressed={isSel} {...activate(() => onSelect({ type: 'station', id: st.id }))}>
              {pill
                ? <rect x={x - 7} y={top - 7} width={14} height={bottom - top + 14} rx={7} fill="#fff" stroke="var(--ll-ink)" strokeWidth={2.5} />
                : ys.map((y, i) => <circle key={i} cx={x} cy={y} r={6} fill="#fff" stroke={COLOR[lines[i]]} strokeWidth={3} />)}
              <rect className="ll-focus-ring" x={x - 11} y={top - 11} width={22} height={bottom - top + 22} rx={10} />
              {isSel && <rect x={x - 11} y={top - 11} width={22} height={bottom - top + 22} rx={10} fill="none" stroke="var(--ll-job)" strokeWidth={2} />}
            </g>
            </Tip>
          )
        })}

        {/* findings: a marker on the rail, a leader to the check */}
        {markers.map((m, i) => {
          const x = m.day ? t.x(m.check?.pay_date || m.day) : null
          if (x == null) return null
          const color = m.severity === 'high' ? 'var(--ll-high)' : m.severity === 'medium' ? 'var(--ll-medium)' : 'var(--gray-500)'
          const payY = lastPay ? laneY('pay', paySegs.find(g => dayMs(g.start) <= dayMs(m.day) && (g.end == null || dayMs(g.end) > dayMs(m.day))) || lastPay) : Y.payTop
          const isSel = selected?.type === 'finding' && selected.id === m.id
          return (
            <Tip key={m.id || i} text={findingHelp(m)} anchor=".ll-rail-mark">
            <g className="ll-hit" tabIndex={0} role="button"
               aria-label={`Finding: ${m.finding.title || 'finding'}${m.check ? `, check paid ${fmtDate(m.check.pay_date)}` : ''}`}
               aria-pressed={isSel} {...activate(() => onSelect({ type: 'finding', id: m.id }))}>
              <line x1={x} x2={x} y1={Y.rail + 9} y2={payY - 8} stroke={color} strokeDasharray="4 3" strokeWidth={1.5} opacity={0.8} />
              <circle cx={x} cy={payY} r={8} fill="none" stroke={color} strokeWidth={3} />
              {m.severity === 'high'
                ? <path className="ll-rail-mark" d={`M${x} ${Y.rail - 9} L${x + 9} ${Y.rail + 7} H${x - 9} Z`} fill={color} />
                : <path className="ll-rail-mark" d={`M${x} ${Y.rail - 9} L${x + 9} ${Y.rail} L${x} ${Y.rail + 9} L${x - 9} ${Y.rail} Z`} fill={color} />}
              <rect className="ll-focus-ring" x={x - 13} y={Y.rail - 13} width={26} height={26} rx={6} />
              {isSel && <circle cx={x} cy={payY} r={13} fill="none" stroke={color} strokeWidth={2} opacity={0.5} />}
            </g>
            </Tip>
          )
        })}

        {/* termini */}
        {visible.map(({ key, label }) => (
          <g key={`t-${key}`}>
            <rect x={RIGHT + 8} y={(key === 'pay' ? (Y.payTop + Y.payBottom) / 2 : Y[key]) - 9} width={label.length * 7 + 12} height={18} rx={4} fill={COLOR[key]} />
            <text className="ll-terminus" x={RIGHT + 14} y={(key === 'pay' ? (Y.payTop + Y.payBottom) / 2 : Y[key]) + 4}>{label}</text>
          </g>
        ))}
      </svg>
    </div>
  )
}
